import axios from 'axios';

import { checkDev } from '../config';
import { filterEventsByStatus } from '../utils/voucherEvent';
import { clearAuthSession, getAuthSession } from '../utils/authStorage';
import { isReauthAvailable, requestReauth } from '../utils/reauthStore';

const LOGIN_PATH = '/login';

// Mocks are loaded on demand so production bundles never download them.
const loadMocks = () => import('./mock');

const baseURL = '/doraemon/api/';

const getUrl = (path) => {
	const basePath = checkDev() ? 'http://doraemon-stg.funkytest.com' : window.location.origin;
	return basePath + baseURL + path;
};

const customFetch = axios.create({
	baseURL,
	headers: {
		'Content-Type': 'application/json',
	},
});

const formFetch = axios.create({
	baseURL,
	headers: {
		'Content-Type': 'multipart/form-data',
	},
});

const attachAuthorization = (config) => {
	const session = getAuthSession();
	if (session?.token) {
		config.headers = config.headers || {};
		config.headers.Authorization = `Bearer ${session.token}`;
	}

	return config;
};

export const backgroundRequestConfig = { suppressLoginRedirect: true };

export const createResponseErrorHandler = (instance) => (error) => {
	if (error.response?.status !== 401) return Promise.reject(error);

	clearAuthSession();
	const config = error.config;
	if (config?.suppressLoginRedirect === true) return Promise.reject(error);
	if (window.location.pathname === LOGIN_PATH) return Promise.reject(error);

	if (isReauthAvailable() && !config?.reauthRetried) {
		return requestReauth(error, () => instance.request({ ...config, reauthRetried: true }));
	}

	window.location.assign(LOGIN_PATH);
	return Promise.reject(error);
};

customFetch.interceptors.request.use(attachAuthorization);
formFetch.interceptors.request.use(attachAuthorization);
customFetch.interceptors.response.use((response) => response, createResponseErrorHandler(customFetch));
formFetch.interceptors.response.use((response) => response, createResponseErrorHandler(formFetch));

const post = (path, request, config) => {
	if (checkDev()) return fakeApi(path, request);
	return customFetch.post(path, request, config);
};

const postFormData = (path, request) => {
	if (checkDev()) return fakeApi(path, request);
	// A caller that needs keys like `Files[0].Type` builds the FormData itself.
	if (request instanceof FormData) return formFetch.post(path, request);
	const formData = new FormData();
	Object.keys(request).forEach((key) => {
		formData.append(key, request[key]);
	});
	return formFetch.post(path, formData);
};

/**
 * Mock payloads normally resolve from the action name as camelCase+Res (see api/mock/).
 * Two kinds of endpoint need an explicit entry: one whose mock breaks that convention, and
 * ones whose action name is not unique across routes - Profile/GetGameProvider and
 * internal/settings-cache/GetGameProvider would otherwise share a payload. Those are keyed
 * by full path so the route, not just the verb, decides.
 */
const MOCK_ACTION_ALIASES = {
	GetDetail: 'getDetailRes',
	'internal/settings-cache/GetOperator': 'cachedOperatorRes',
	'internal/settings-cache/GetGameProvider': 'cachedGameProviderRes',
	'Games/SearchGames': 'cachedGamesRes',
	'internal/settings-cache/GetLobby': 'cachedLobbyRes',
};

/** Endpoints that return a raw JSON array (not { returnCode, ... }). */
const MOCK_LIST_ACTIONS = new Set(['SearchVoucherEvent']);

const resolveMockPayload = (mocks, actionName, path) => {
	const mockName =
		MOCK_ACTION_ALIASES[path] ??
		MOCK_ACTION_ALIASES[actionName] ??
		actionName.charAt(0).toLowerCase() + actionName.slice(1) + 'Res';
	return mocks[mockName];
};

const fakeApi = async (path, request) => {
	const mocks = await loadMocks();
	const actionName = path.split('/').at(-1).split('?')[0];
	if (actionName === 'GoogleLogin') {
		return {
			status: 200,
			statusText: 'OK',
			data: {
				returnCode: '0',
				message: '',
				token: 'dev-token',
				email: 'gary.chen@funkygamesltd.com',
				name: 'Gary Chen',
				expiresAt: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
				gameRiskRole: 'query_edit_unblock',
				gameRiskPermissions: ['query', 'edit', 'unblock'],
			},
			request,
		};
	}
	if (MOCK_LIST_ACTIONS.has(actionName)) {
		const payload = resolveMockPayload(mocks, actionName, path);
		const list = Array.isArray(payload) ? payload : payload?.list ?? [];
		const filtered =
			actionName === 'SearchVoucherEvent'
				? filterEventsByStatus(list, request?.status)
				: list;
		return {
			status: 200,
			statusText: 'OK',
			data: filtered,
			request,
		};
	}
	if (actionName === 'GetDetail') {
		return {
			status: 200,
			statusText: 'OK',
			data: { returnCode: '0', message: '', ...mocks.resolveGetDetailMock(request?.fpId) },
			request,
		};
	}
	if (actionName === 'GetOperatorList') {
		return {
			status: 200,
			statusText: 'OK',
			data: {
				returnCode: '0',
				message: '',
				operators: mocks.filterOperatorListMock(request?.keyword),
			},
			request,
		};
	}
	if (actionName === 'GetRootOperatorList') {
		return {
			status: 200,
			statusText: 'OK',
			data: {
				returnCode: '0',
				message: '',
				operators: mocks.getRootOperatorListRes.operators,
			},
			request,
		};
	}
	if (actionName === 'GetMasterOperatorList') {
		return {
			status: 200,
			statusText: 'OK',
			data: {
				returnCode: '0',
				message: '',
				operators: mocks.getMasterOperatorListRes.operators,
			},
			request,
		};
	}
	return {
		status: 200,
		statusText: 'OK',
		data: { returnCode: '0', message: '', ...resolveMockPayload(mocks, actionName, path) },
		request,
	};
};
const fakeGetApi = async (path, request) => {
	const mocks = await loadMocks();
	const actionName = path.split('/').at(-1).split('?')[0];
	return { status: 200, statusText: 'OK', data: resolveMockPayload(mocks, actionName, path), request };
};

export const api = {
	googleLogin: async (idToken, config) => await post('Login/GoogleLogin', { idToken }, config),
	getOperatorInfo: async (searchParams) =>
		await post(`Profile/GetOperatorInfo?search=${encodeURIComponent(searchParams ?? '')}`),
	getMainOperatorInfo: async () => await post('/Profile/GetMainOperatorInfo', {}),
	operatorInfoDetail: async (fpId) => await post('Profile/OperatorInfoDetail', { fpId }),
	getGameProvider: async () => await post('Profile/GetGameProvider'),
	gameProviderInfoDetail: async (userAgent) => await post('Profile/GameProviderInfoDetail', { userAgent }),
	searchRunningBets: async (fpId) => await post('Bet/SearchRunningBets', { fpId }),
	forceCancelRunningBet: async ({ refNoList }) => await post('Bet/forceCancelRunningBet', { refNoList }),
	cancelBet: async ({ playerId, refNo, fpId, userAgent }) =>
		await post('Bet/CancelBet', { playerId, refNo, fpId, userAgent }),
	recoverGameBetHistory: async ({ refNoList, startTime, endTime }) =>
		await post('Recover/RecoverGameBetHistory', { refNoList, startTime, endTime }),
	refreshAppSetting: async () => await post('Mgmt/RefreshAppSettings'),
	getGameRules: async (eventType, startTime, endTime) =>
		await post('Games/GetGameRules', { eventType, startTime, endTime }),
	reloadSetting: async () => await post('Games/ReloadSetting'),
	createGameRule: async ({ fpId, operatorName, gameProvider, gameCode, eventType, startTime, endTime }) =>
		await post('Games/CreateGameRule', { fpId, operatorName, gameProvider, gameCode, eventType, startTime, endTime }),
	updateGameRule: async ({ ruleId, startTime, endTime, status }) =>
		await post('Games/UpdateGameRule', { ruleId, startTime, endTime, status }),
	getGameCodeByProvider: async (userAgent) => await post('Profile/GetGameCodeByProvider', { userAgent }),
	cloneLobbyTemplate: async ({ sourceSettingName, newSettingName }) =>
		await post('Lobby/CloneLobbyTemplate', { sourceSettingName, newSettingName }),
	deleteLobbyTemplate: async (settingName) => await post('Lobby/DeleteLobbyTemplate', { settingName }),
	searchLobbyTemplateList: async () => await post('Lobby/SearchLobbyTemplateList'),
	getLobbyTemplate: async (settingName) => await post('Lobby/GetLobbyTemplate', { settingName }),
	updateLobbyTemplate: async (settingName, data) => await post('Lobby/UpdateLobbyTemplate', { settingName, data }),
	reloadOperatorLobbySetting: async () => await post('Lobby/ReloadOperatorLobbySetting'),
	syncLobbySettingGame: async () => await post('Lobby/SyncLobbySettingGame'),
	getActiveGame: async (userAgent) => await post('Games/GetActiveGame', { userAgent }),
	updateGameOrder: async (PresentationUpdateGameInfos = []) =>
		await post('Games/UpdateGameOrder', { PresentationUpdateGameInfos }),
	getGameInfoList: async () => await post('GameInfo/GetGameInfoList'),
	createGameInfo: async (gameInfo) => await post('GameInfo/CreateGameInfo', { gameInfo }),
	updateGameInfo: async (gameInfo) => await post('GameInfo/UpdateGameInfo', { gameInfo }),
	deleteGameInfo: async (gameCode) => await post('GameInfo/DeleteGameInfo', { gameCode }),
	getGameNameSummary: async () => await post('GameInfo/GetGameNameSummary'),
	getGameNames: async (gameCode) => await post('GameInfo/GetGameNames', { gameCode }),
	setGameName: async ({ gameCode, languageCode, value }) =>
		await post('GameInfo/SetGameName', { gameCode, languageCode, value }),
	deleteGameName: async ({ gameCode, languageCode }) =>
		await post('GameInfo/DeleteGameName', { gameCode, languageCode }),
	getGameCertFiles: async (gameCode) => await post('GameCert/GetGameCertFiles', { gameCode }),
	uploadGameCertFiles: async (formData) => await postFormData('GameCert/UploadGameCertFiles', formData),
	deleteGameCertFiles: async ({ gameCode, jurisdiction }) =>
		await post('GameCert/DeleteGameCertFiles', { gameCode, jurisdiction }),
	updateLobbyToggle: async (presentationUpdateLobbyToggles) =>
		await post('Lobby/UpdateLobbyToggle', { presentationUpdateLobbyToggles }),
	getLobbyGameIconByLang: (lang, gameCode) => getUrl(`Lobby/GetLobbyGameIconByLang/${lang}/${gameCode}`),
	getImageById: ({ lang, category, imageId }) => getUrl(`Lobby/GetImageById/${lang}/${category}/${imageId}`),
	uploadLobbyGameIcon: async (image, gameCode, lang) =>
		await postFormData('Lobby/UploadLobbyGameIcon', { image, gameCode, lang }),
	uploadBannerImage: async ({ image, category, lang }) =>
		await postFormData('Lobby/UploadBannerImage', { image, category, lang }),
	getBannerList: async (settingName, bannerInfo) => await post('Lobby/GetBannerList', { settingName, bannerInfo }),
	createOrUpdateBannerInfo: async ({ settingName, bannerList }) =>
		await post('Lobby/CreateOrUpdateBannerInfo', { settingName, bannerInfo: bannerList }),
	createOrUpdateNewTopOneGame: async ({ settingName = '', gameCode = '', imageId = [] }) =>
		await post('Lobby/CreateOrUpdateNewTopOneGame', { settingName, gameCode, imageId }),
	getNewTopOneGame: async (settingName = '') => await post('Lobby/GetNewTopOneGame', { settingName }),
	getExchangeRate: async (body) => await post('ExchangeRate/GetExchangeRate', body),
	getCurrencyList: async (body) => await post('ExchangeRate/GetCurrencyList', body),
	uploadExchangeRate: async (body) => await post('ExchangeRate/UploadExchangeRate', body),
	getSubLineRequisitions: async (body, config) => await post('SubLine/GetSubLineRequisitions', body, config),
	getSubLineInfos: async (body) => await post('SubLine/GetSubLineInfos', body),
	approveSubLineRequisition: async (requisitionId) =>
		await post('SubLine/ApproveSubLineRequisition', { requisitionId }),
	rejectSubLineRequisition: async (requisitionId) => await post('SubLine/RejectSubLineRequisition', { requisitionId }),
	getRootOperatorList: async () => await post('Operator/GetRootOperatorList', {}),
	getMasterOperatorList: async () => await post('Operator/GetMasterOperatorList', {}),
	getOperatorList: async (keyword) =>
		await post('Operator/GetOperatorList', { keyword: keyword ?? '', includeSuspended: true }),
	createRootOperator: async (body) => await post('Operator/CreateRoot', body),
	createMemberOperator: async (body) => await post('Operator/CreateMember', body),
	getOperatorDetail: async (fpId) => await post('Operator/GetDetail', { fpId }),
	updateOperator: async (body) => await post('Operator/Update', body),
	deactivateOperator: async (fpId) => await post('Operator/Deactivate', { fpId }),
	activateOperator: async (fpId) => await post('Operator/Activate', { fpId }),
	refreshOperators: async () => await post('Operator/RefreshOperators', {}),
	getGameTemplateSetting: async (id) => await post('GameTemplate/GetGameTemplateSetting', { id }),
	createGameTemplateSetting: async (body) => await post('GameTemplate/CreateGameTemplateSetting', body),
	updateGameTemplateSetting: async (body) => await post('GameTemplate/UpdateGameTemplateSetting', body),
	setDefaultGameTemplate: async (id) => await post('GameTemplate/SetDefaultGameTemplate', { id }),
	deleteGameTemplateSetting: async (id) => await post('GameTemplate/DeleteGameTemplateSetting', { id }),
	getOperatorGameTemplateRelation: async (fpId, gameTemplateId) =>
		await post('GameTemplate/GetOperatorGameTemplateRelation', { fpId, gameTemplateId }),
	setOperatorGameTemplateRelation: async (req) => await post('GameTemplate/SetOperatorGameTemplateRelation', req),
	deleteOperatorGameTemplateRelation: async (fpId) =>
		await post('GameTemplate/DeleteOperatorGameTemplateRelation', { fpId }),
	getGameMappingTypes: async (request) => await post('GameTemplate/GetGameMappingTypes', request),
	getGameMappingNames: async () => await post('GameTemplate/GetGameMappingNames'),
	searchGameMappingInfos: async (request) => await post('GameTemplate/SearchGameMappingInfos', request),
	setGameMappingInfos: async (request) => await post('GameTemplate/SetGameMappingInfos', request),
	deleteGameMappingInfos: async (request) => await post('GameTemplate/DeleteGameMappingInfos', request),
	getOpGameList: async (body) => await post('Profile/GetOpGameList', body),
	getOpLobbyGameList: async (body) => await post('Profile/GetOpLobbyGameList', body),
	getTargetCurrencyList: async (body) => await post('ExchangeRate/GetTargetCurrencyList', body),
	getShardId: async (body) => {
		if (checkDev()) return fakeGetApi('Recover/GetShardId');
		return await customFetch.get(`Recover/GetShardId/${body}`);
	},
	// ── Player Risk Levels ──
	getPlayerLevels: async () => {
		if (checkDev()) return { status: 200, data: (await loadMocks()).getPlayerLevelsRes };
		return await customFetch.get('internal/risk/player-levels');
	},
	updatePlayerLevel: async (riskLevel, body) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.put(`internal/risk/player-levels/${riskLevel}`, body);
	},
	deletePlayerLevel: async (riskLevel) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.delete(`internal/risk/player-levels/${riskLevel}`);
	},
	// ── Game Total Risk Levels ──
	getGameLevels: async () => {
		if (checkDev()) return { status: 200, data: (await loadMocks()).getGameLevelsRes };
		return await customFetch.get('internal/risk/game-levels');
	},
	updateGameLevel: async (riskLevel, body) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.put(`internal/risk/game-levels/${riskLevel}`, body);
	},
	deleteGameLevel: async (riskLevel) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.delete(`internal/risk/game-levels/${riskLevel}`);
	},
	// ── Game Risk Mapping ──
	getMappings: async () => {
		if (checkDev()) return { status: 200, data: (await loadMocks()).getMappingsRes };
		return await customFetch.get('internal/risk/mappings');
	},
	updateMapping: async (body) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.put('internal/risk/mappings', body);
	},
	deleteMapping: async (gameCode) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success' } };
		return await customFetch.delete(`internal/risk/mappings/${gameCode}`);
	},
	getRiskBlacklist: async (params) => {
		if (checkDev()) return { status: 200, data: (await loadMocks()).getRiskBlacklistRes };
		const { status, fpId, ...rest } = params;
		const query = new URLSearchParams(
			Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== '' && v != null))
		);
		const fpNum = fpId !== '' && fpId != null ? Number(fpId) : NaN;
		if (!Number.isNaN(fpNum) && fpNum > 0) {
			query.append('fpId', String(fpNum));
		}
		if (status && status !== 'ALL' && status !== '') {
			query.append('status', status);
		}
		const qs = query.toString();
		return await customFetch.get(`internal/risk/blacklist${qs ? `?${qs}` : ''}`);
	},
	removeRiskBlacklist: async (body) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: '' } };
		return await customFetch.post('internal/risk/blacklist/remove', body);
	},
	/** 將 Status=Active 且 ExpiredAt 已過期之列改為 Expired（維運／排程用） */
	syncRiskBlacklistExpired: async () => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: '' } };
		return await customFetch.post('Scheduler/SyncExpiredBlacklist', {});
	},
	/** 取得風險玩家黑名單封鎖是否啟用（PresentationGetBlackRiskPlayerResponse） */
	getBlackRiskPlayer: async () => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: '', enabled: true } };
		return await customFetch.get('internal/risk/black-risk-player');
	},
	/** 啟用／停用風險玩家黑名單封鎖（PresentationSetBlackRiskPlayerRequest） */
	setBlackRiskPlayer: async (body) => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success', ...body } };
		return await customFetch.put('internal/risk/black-risk-player', body);
	},
	/** 解除所有目前為 Active 的黑名單 */
	clearAllActiveRiskBlacklist: async () => {
		if (checkDev()) return { status: 200, data: { returnCode: '0', message: 'Success', clearedCount: 0 } };
		return await customFetch.post('internal/risk/blacklist/clear-active', {});
	},
	searchVoucherEvents: async ({ status }) => await post('VoucherEvent/SearchVoucherEvent', { status }),
	getVoucherEvent: async (eventId) => await post('VoucherEvent/GetVoucherEvent', { eventId }),
	createVoucherEvent: async (body) => await post('VoucherEvent/CreateVoucherEvent', body),
	closeVoucherEvent: async (eventId) => await post('VoucherEvent/CloseVoucherEvent', { eventId }),
	getCachedOperator: async (body) => await post('internal/settings-cache/GetOperator', body),
	getCachedGameProvider: async (body) => await post('internal/settings-cache/GetGameProvider', body),
	getCachedGames: async (body) => await post('Games/SearchGames', body),
	getCachedLobby: async (body) => await post('internal/settings-cache/GetLobby', body),
	getOperatorFamilies: async () => {
		if (checkDev()) return { status: 200, data: (await loadMocks()).operatorFamiliesRes };
		return await customFetch.get('internal/settings-cache/GetOperatorFamilies');
	},
};

